import { AnalysisSettings, LoadSettings } from '@paragon/contract-library';

import { RootState } from '..';
import { SettingsDatabaseState } from '../slices/settingsDatabaseSlice';
import { getActiveComponentDesign } from './getActiveComponentDesignSelector';
import { isViewingOldAnalysisSet } from './viewOnlyModeSelectors';

interface SettingsDatabaseOptions {
  // This option should be used when you want to check the settings that actually exist on the component design itself,
  //   without taking into account whether or not an analysis set is being previewed.
  ignoreAnalysisSetPreviews?: boolean;
}

export function getLoadSettings(state: RootState, componentDesignGuid: string, options: SettingsDatabaseOptions = {}) {
  return getSettings(componentDesignGuid, 'loadSettings', options)(state) as LoadSettings;
}

export function getAnalysisSettings(
  state: RootState,
  componentDesignGuid: string,
  options: SettingsDatabaseOptions = {},
) {
  return getSettings(componentDesignGuid, 'analysisSettings', options)(state) as AnalysisSettings;
}

const getSettings =
  <T extends keyof Pick<SettingsDatabaseState, 'analysisSettings' | 'loadSettings'>>(
    componentDesignGuid: string,
    settingsType: T,
    options: SettingsDatabaseOptions = {},
  ) =>
  (state: RootState) => {
    const activeComponent = getActiveComponentDesign(state);

    const baseSettings = state.settingsDatabase[settingsType][componentDesignGuid];
    if (options.ignoreAnalysisSetPreviews) {
      return baseSettings;
    }

    if (
      activeComponent?.guid === componentDesignGuid &&
      state.analysisSets.activeAnalysisSetGuid &&
      isViewingOldAnalysisSet(state)
    ) {
      const activeAnalysisSet = state.analysisSets.sets.find(
        (set) => set.guid === state.analysisSets.activeAnalysisSetGuid,
      );

      if (activeAnalysisSet && activeAnalysisSet[settingsType]) {
        return activeAnalysisSet[settingsType];
      }
    }

    return baseSettings;
  };
